import React, { useState, useEffect } from 'react';
import {
  Alert,
  Checkbox,
  Box,
  Typography,
  Stack,
  CircularProgress,
  Sheet,
} from '@mui/joy';
import type { Task } from '../../types/tasks';
import { TaskCard } from './TaskCard';
import { TaskEditModal } from './TaskEditModal';
import { TaskDetailModal } from './TaskDetailModal';
import { useTaskMutations } from '../../hooks/useTasks';

interface TaskListProps {
  tasks: Task[];
  isLoading: boolean;
  onTaskUpdate: () => void;
  variant?: 'full' | 'compact';
  onOpenTask?: (task: Task) => void;
  selectedIds?: number[];
  onSelectTask?: (id: number) => void;
}

export const TaskList: React.FC<TaskListProps> = ({ 
  tasks, 
  isLoading, 
  onTaskUpdate,
  onOpenTask, selectedIds, onSelectTask,
  variant = 'full'
}) => {
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [fadingTasks, setFadingTasks] = useState<Set<number>>(new Set());
  const [visibleTasks, setVisibleTasks] = useState<Task[]>(tasks);

  // Use shared task mutations
  const {
    completeTask: completeTaskMutation,
    updateStatus: updateStatusMutation,
  } = useTaskMutations();

  // Initialize visible tasks
  useEffect(() => {
    // Only update if we don't have fading tasks
    if (fadingTasks.size === 0) {
      setVisibleTasks(tasks);
    } else {
      // Preserve fading tasks that might have been removed from the tasks list
      const fadingTasksList = visibleTasks.filter(task => fadingTasks.has(task.id));
      const nonFadingTasks = tasks.filter(task => !fadingTasks.has(task.id));
      const combined = [...nonFadingTasks, ...fadingTasksList];
      const uniqueTasks = Array.from(new Map(combined.map(task => [task.id, task])).values());
      setVisibleTasks(uniqueTasks);
    }
  }, [tasks]);

  const handleTaskClick = (task: Task) => {
    if (onOpenTask) { onOpenTask(task); return; }
    setSelectedTask(task);
    setIsDetailModalOpen(true);
  };

  const handleEditClick = (task: Task) => {
    setSelectedTask(task);
    setIsEditModalOpen(true);
  };

  const handleToggleComplete = (taskId: number, completed: boolean) => {
    if (completed) {
      // Start fade animation immediately
      setFadingTasks(prev => new Set(prev).add(taskId));
      
      // Delay the mutation to allow animation to start
      setTimeout(() => {
        completeTaskMutation.mutate(taskId, {
          onError: () => { setFadingTasks(prev => { const next = new Set(prev); next.delete(taskId); return next; }); },
          onSuccess: () => {
            // After mutation succeeds, wait for animation to finish then clean up
            setTimeout(() => {
              setFadingTasks(prev => {
                const newSet = new Set(prev);
                newSet.delete(taskId);
                return newSet;
              });
              setVisibleTasks(prev => prev.filter(t => t.id !== taskId));
              onTaskUpdate();
            }, 400); // Slightly less than animation duration to ensure cleanup
          }
        });
      }, 100); // Small delay to ensure animation starts first
    } else {
      updateStatusMutation.mutate({ 
        taskId, 
        status: 'not_started' 
      });
    }
  };

  const handleMenuClick = (task: Task) => {
    setSelectedTask(task);
    handleEditClick(task);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (tasks.length === 0) {
    return (
      <Sheet variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 'sm' }}>
        <Typography level="body-lg" sx={{ mb: 2 }}>
          No tasks found
        </Typography>
        <Typography level="body-sm" color="neutral">
          Create your first task to get started
        </Typography>
      </Sheet>
    );
  }

  return (
    <>
      {(completeTaskMutation.isError || updateStatusMutation.isError) && <Alert color="danger">The task could not be updated. Complete prerequisites and checklist items, then retry.</Alert>}
      <Stack spacing={2}>
        {visibleTasks.map((task) => {
          const isFading = fadingTasks.has(task.id);
          return (
            <Box
              key={task.id}
              sx={{
                opacity: isFading ? 0 : 1,
                transform: isFading ? 'scale(0.95) translateX(-30px)' : 'scale(1) translateX(0)',
                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                backgroundColor: isFading ? 'var(--joy-palette-success-100)' : 'transparent',
                borderRadius: 'sm',
                overflow: 'hidden',
              }}
            >
              {onSelectTask && <Checkbox sx={{ m: 1 }} label={`Select ${task.name}`} checked={selectedIds?.includes(task.id) || false} onChange={() => onSelectTask(task.id)} />}
              <TaskCard
                task={task}
                variant={variant}
                onClick={handleTaskClick}
                onToggleComplete={handleToggleComplete}
                onMenuClick={handleMenuClick}
              />
            </Box>
          );
        })}
      </Stack>

      {/* Edit Modal */}
      {selectedTask && (
        <TaskEditModal
          open={isEditModalOpen}
          task={selectedTask}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedTask(null);
          }}
          onSuccess={() => {
            setIsEditModalOpen(false);
            setSelectedTask(null);
            onTaskUpdate();
          }}
        />
      )}

      {/* Detail Modal */}
      {selectedTask && (
        <TaskDetailModal
          open={isDetailModalOpen}
          task={selectedTask}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedTask(null);
          }}
          onUpdate={onTaskUpdate}
          onTaskUpdated={(updatedTask) => {
            setSelectedTask(updatedTask);
          }}
        />
      )}
    </>
  );
};